import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, MapPin, User, CalendarPlus } from "lucide-react";
import { useArtisanAppointments } from "@/hooks/useArtisanAppointments";
import { generateGoogleCalendarUrl } from "@/hooks/useAppointments";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";

const DashboardUpcomingAppointments = () => {
  const { t, locale } = useLanguage("artisan");
  const lp = useLocalizedPath();
  const { data: allAppointments = [], isLoading } = useArtisanAppointments("all");
  const loc = locale === "ar" ? "ar-DZ" : "fr-FR";

  const statusLabel: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    proposed:        { label: t("upcoming_rdv.status_proposed"), variant: "secondary" },
    counterProposed: { label: t("upcoming_rdv.status_counter"),  variant: "outline" },
    accepted:        { label: t("upcoming_rdv.status_accepted"), variant: "default" },
  };

  const locationLabel = (apt: any) =>
    apt.location_type === "call"
      ? `📞 ${t("upcoming_rdv.location_call")}`
      : apt.location_type === "clientAddress"
        ? `🏠 ${apt.location_text || t("upcoming_rdv.location_client")}`
        : `📍 ${apt.location_text}`;

  // Only show upcoming (accepted/proposed/counterProposed), sorted by date, max 3
  const upcoming = allAppointments
    .filter((a: any) => ["accepted", "proposed", "counterProposed"].includes(a.status))
    .filter((a: any) => new Date(a.starts_at) >= new Date())
    .sort((a: any, b: any) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
    .slice(0, 3);

  if (isLoading) {
    return (
      <section className="space-y-3">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          {t("upcoming_rdv.title")}
        </h2>
        <div className="py-6 text-center text-sm text-muted-foreground">{t("upcoming_rdv.loading")}</div>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          {t("upcoming_rdv.title")}
        </h2>
        <a href={lp("/mes-rendez-vous")}>
          <Button variant="ghost" size="sm" className="text-xs">{t("upcoming_rdv.see_all")}</Button>
        </a>
      </div>

      {upcoming.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-center">
            <Calendar className="mx-auto h-8 w-8 text-muted-foreground/30" />
            <p className="mt-2 text-sm text-muted-foreground">{t("upcoming_rdv.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {upcoming.map((apt: any) => {
            const config = statusLabel[apt.status] || statusLabel.proposed;
            const startDate = new Date(apt.starts_at);

            return (
              <Card key={apt.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={config.variant} className="text-[10px]">{config.label}</Badge>
                        <span className="text-xs text-muted-foreground font-medium">
                          {startDate.toLocaleDateString(loc, { weekday: "short", day: "numeric", month: "short" })}
                          {" · "}
                          {startDate.toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm">
                        <User className="h-3.5 w-3.5 text-primary shrink-0" />
                        <span className="font-medium truncate">{apt.client_name}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {locationLabel(apt)}
                      </div>
                    </div>

                    {apt.status === "accepted" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px] px-2 shrink-0"
                        onClick={() => window.open(generateGoogleCalendarUrl(apt), "_blank")}
                      >
                        <CalendarPlus className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default DashboardUpcomingAppointments;
